import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, Plus, Pencil } from "lucide-react";
import { useContacts, useCreateContact, usePatchContact, CONTACT_ROLES, type Contact, type ContactRole } from "@/hooks/useContacts.js";
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from "@/components/ui/table.js";
import { Badge } from "@/components/ui/badge.js";
import { Button } from "@/components/ui/button.js";
import { Input } from "@/components/ui/input.js";

// Anagrafica unica: una persona, più ruoli. La stessa scheda segue il cliente
// da acquirente a venditore senza duplicati (dedup su email lato API).

export default function Contacts(): JSX.Element {
  const [q, setQ] = useState("");
  const [role, setRole] = useState<string>("");
  const [editing, setEditing] = useState<Contact | "new" | null>(null);
  const { data, isLoading } = useContacts({ q: q || undefined, role: role || undefined });
  const items = data?.items ?? [];

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-h1 font-display text-ink">Contatti</h1>
          <p className="text-body text-ink-soft">{data?.total ?? 0} anagrafiche · una persona, più ruoli</p>
        </div>
        <Button onClick={() => setEditing("new")}><Plus className="size-4" /> Nuovo contatto</Button>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Cerca nome, email, telefono…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-[320px]" />
        <div className="flex flex-wrap gap-1.5">
          <RoleChip label="tutti" active={role === ""} onClick={() => setRole("")} />
          {CONTACT_ROLES.map((r) => <RoleChip key={r} label={r} active={role === r} onClick={() => setRole(r)} />)}
        </div>
      </div>

      {editing ? (
        <ContactForm
          key={editing === "new" ? "new" : editing.id}
          contact={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}

      <div className="overflow-x-auto rounded border border-rule bg-surface">
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Nome</TableHeader>
              <TableHeader>Ruoli</TableHeader>
              <TableHeader>Contatti</TableHeader>
              <TableHeader className="w-[160px]">Creato</TableHeader>
              <TableHeader className="w-[60px]" />
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <p className="text-body-strong text-ink">{row.name}</p>
                  {row.notes ? <p className="max-w-[380px] truncate text-meta text-ink-mute">{row.notes}</p> : null}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {row.roles.length ? row.roles.map((r) => <Badge key={r}>{r}</Badge>) : <span className="text-meta text-ink-mute">—</span>}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap items-center gap-2 text-meta text-ink-soft">
                    {row.email ? <a href={`mailto:${row.email}`} className="inline-flex items-center gap-1 text-accent hover:underline"><Mail className="size-3" /> {row.email}</a> : null}
                    {row.phone ? <a href={`tel:${row.phone}`} className="inline-flex items-center gap-1 text-accent hover:underline"><Phone className="size-3" /> {row.phone}</a> : null}
                    {!row.email && !row.phone ? "—" : null}
                  </div>
                </TableCell>
                <TableCell className="font-mono text-meta text-ink-mute">{new Date(row.created_at).toLocaleDateString("it-IT")}</TableCell>
                <TableCell>
                  <button className="text-ink-mute hover:text-accent" onClick={() => setEditing(row)} aria-label={`Modifica ${row.name}`}><Pencil className="size-4" /></button>
                </TableCell>
              </TableRow>
            ))}
            {!isLoading && items.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="py-12 text-center text-ink-mute">Nessun contatto. Crea il primo con "Nuovo contatto".</TableCell></TableRow>
            ) : null}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function RoleChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }): JSX.Element {
  return (
    <button
      onClick={onClick}
      className={`rounded-full border px-3 py-1 text-meta transition-colors ${active ? "border-accent bg-accent/10 text-accent" : "border-rule text-ink-soft hover:border-accent/50"}`}
    >
      {label}
    </button>
  );
}

function ContactForm({ contact, onClose }: { contact: Contact | null; onClose: () => void }): JSX.Element {
  const create = useCreateContact();
  const patch = usePatchContact();
  const [name, setName] = useState(contact?.name ?? "");
  const [email, setEmail] = useState(contact?.email ?? "");
  const [phone, setPhone] = useState(contact?.phone ?? "");
  const [notes, setNotes] = useState(contact?.notes ?? "");
  const [roles, setRoles] = useState<ContactRole[]>((contact?.roles ?? []) as ContactRole[]);
  const busy = create.isPending || patch.isPending;

  function toggleRole(r: ContactRole) {
    setRoles((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]));
  }

  async function save() {
    const input = { name: name.trim(), email: email.trim() || null, phone: phone.trim() || null, notes: notes.trim() || null, roles };
    try {
      if (contact) await patch.mutateAsync({ id: contact.id, ...input });
      else await create.mutateAsync(input);
      toast.success(contact ? "Contatto aggiornato" : "Contatto creato");
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Errore salvataggio");
    }
  }

  return (
    <div className="space-y-4 rounded border border-accent/40 bg-surface p-5">
      <h2 className="text-body-strong text-ink">{contact ? `Modifica · ${contact.name}` : "Nuovo contatto"}</h2>
      <div className="grid gap-3 sm:grid-cols-3">
        <Input placeholder="Nome e cognome *" value={name} onChange={(e) => setName(e.target.value)} />
        <Input placeholder="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <Input placeholder="Telefono" value={phone} onChange={(e) => setPhone(e.target.value)} />
      </div>
      <div className="flex flex-wrap gap-1.5">
        {CONTACT_ROLES.map((r) => <RoleChip key={r} label={r} active={roles.includes(r)} onClick={() => toggleRole(r)} />)}
      </div>
      <Input placeholder="Note (es. cerca trilocale Bardolino, budget 300k)" value={notes} onChange={(e) => setNotes(e.target.value)} />
      <div className="flex gap-2">
        <Button onClick={() => void save()} disabled={busy || name.trim().length < 2}>{busy ? "Salvo…" : "Salva"}</Button>
        <Button variant="ghost" onClick={onClose}>Annulla</Button>
      </div>
    </div>
  );
}
